import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_UTILITY_ACTIVATION_CREATE,
  SR_API_ACTION_UTILITY_ACTIVATION_CREATE_LAZY_SIGNUP,
} from 'api/utilityActionTypes';

import {
  hasRoleRenter,
  hasRoleAdmin,
  UTILITY_USER_STATUS_PENDING_SIGNUP,
  UTILITY_USER_SESSION_PROVIDER_VERIFICATION_CODE,
} from 'common/utility/userRoles';

import {
  UTILITY_ADDRESS_SEARCH_STATUS_FAILED,
  UTILITY_ADDRESS_SEARCH_STATUS_SUCCESS,
  UTILITY_PARTNER_CUSTOMER_TYPE_TENANT_SCREENING,
} from 'common/utility/constants';

import { isUtilityActivationActive } from 'common/utility/helpers';

import AddressSearchActions from 'client/actions/AddressSearch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrUtilitySetupPage from 'client/components/SrUtilitySetup/SrUtilitySetupPage';
import SrUtilityAddressSearchForm from 'client/components/SrUtilitySetup/SrUtilityAddressSearchForm';
import SrUtilityRecentlyUnlistedAddresses from 'client/components/SrUtilitySetup/SrUtilityRecentlyUnlistedAddresses/SrUtilityRecentlyUnlistedAddresses';

import './SrUtilitySetupAddressSearchPage.less';

const SrUtilitySetupAddressSearchPage = ({
  activationLinkRequest,
  utilityUser,
  utilityPartner,
  recentlyUnlistedAddresses,
  dispatch,
}) => {
  const history = useHistory();
  const isRenter = hasRoleRenter(utilityUser.roles);
  const isAdmin = hasRoleAdmin(utilityUser.roles);
  const isTenantScreeningPartner =
    utilityPartner.customerType === UTILITY_PARTNER_CUSTOMER_TYPE_TENANT_SCREENING;

  const trackFailedSearch = ({ addressInput }) => {
    dispatch(AddressSearchActions.track, {
      searchInput: addressInput,
      status: UTILITY_ADDRESS_SEARCH_STATUS_FAILED,
      utilityPartnerId: utilityPartner.id,
    });
  };

  const trackSuccessfulSearch = async ({ addressId, searchInput, callback }) => {
    const trackPayload = {
      status: UTILITY_ADDRESS_SEARCH_STATUS_SUCCESS,
      utilityPartnerId: utilityPartner.id,
    };

    if (addressId) {
      trackPayload.addressId = addressId;
    } else {
      trackPayload.searchInput = searchInput;
    }

    return dispatch(AddressSearchActions.track, trackPayload).then(() => {
      if (addressId) {
        return dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
          payload: {
            addressId,
            utilityPartnerId: utilityPartner.id,
          },
          onSuccess: callback,
        });
      }
      return callback();
    });
  };

  const handleCreateUtilityActivation = ({ address }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_CREATE,
      payload: {
        utilityPartner: {
          id: utilityPartner.id,
        },
        address: address,
        utilityUserId: utilityUser.id,
      },
      onSuccess: (newActivation) => {
        history.push(`/utility-setup/${utilityPartner.slug}/${newActivation.shortId}`);
      },
    });
  };

  const onLoadActivation = ({ address, unitNumber, utilityActivation }) => {
    if (!address) {
      return;
    }

    // User
    const { user = {} } = utilityActivation || {};
    const { status = '', sessionProvider = '' } = user || {};
    const isPendingSignup =
      !utilityActivation?.user || status === UTILITY_USER_STATUS_PENDING_SIGNUP;
    const needsVerificationCode =
      sessionProvider === UTILITY_USER_SESSION_PROVIDER_VERIFICATION_CODE;

    // Utility Activation
    const _isUtilityActivationActive = isUtilityActivationActive(utilityActivation);

    // User is logged-in and is an admin.
    if (isAdmin) {
      // Redirect admin to the utility activation.
      if (_isUtilityActivationActive) {
        history.push(`/utility-setup/${utilityPartner.slug}/${utilityActivation.shortId}`);
      } else {
        // Create utility activation with lazy signup and redirect admin to it.
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_ACTIVATION_CREATE_LAZY_SIGNUP,
          payload: {
            utilityPartnerId: utilityPartner.id,
            addressId: address.id,
          },
          onSuccess: (newActivation) => {
            history.push(`/utility-setup/${utilityPartner.slug}/${newActivation.shortId}`);
          },
        });
      }
      return;
    }

    // User is logged-in and is a renter.
    if (isRenter) {
      if (!_isUtilityActivationActive) {
        // Create utility activation and redirect user to it.
        return handleCreateUtilityActivation({ address });
      }
      if (utilityActivation.user.id === utilityUser.id) {
        // Redirect user to they utility activation.
        return history.push(`/utility-setup/${utilityPartner.slug}/${utilityActivation.shortId}`);
      }
    } else if (!_isUtilityActivationActive && isTenantScreeningPartner) {
      // Redirect user to the signup form, preserving the address place_id and unit number.
      return history.push(
        `/utility-setup/${utilityPartner.slug}/signup/${address.place_id || address.googlePlaceId}${
          unitNumber ? `?unitNumber=${unitNumber}` : ''
        }`,
      );
    } else if (_isUtilityActivationActive) {
      // User is pending signup.
      if (isPendingSignup) {
        return history.push(`/utility-setup/${utilityPartner.slug}/signup/${address.id}`);
      }

      // User needs verification code.
      if (needsVerificationCode) {
        return history.push(`/utility-setup/${utilityPartner.slug}/verify-phone/${address.id}`);
      }

      // User and activation were already created. Redirect to login.
      return history.push(`/utility-setup/${utilityPartner.slug}/login/${address.id}`);
    }

    // Redirect user to the signup form.
    return history.push(`/utility-setup/${utilityPartner.slug}/signup/${address.id}`);
  };

  const onAddressSearchSubmit = ({ address, addressInput, unitNumber }) => {
    if (!address.id && !isTenantScreeningPartner) {
      return;
    }

    return trackSuccessfulSearch({
      addressId: address.id,
      searchInput: addressInput,
      callback: (utilityActivation) => {
        onLoadActivation({ address, unitNumber, utilityActivation });
      },
    });
  };

  return (
    <SrUtilitySetupPage logoUrl={utilityPartner.logoUrl}>
      <div styleName="view">
        {activationLinkRequest?.failureReason && (
          <div styleName="activationLinkRequestBox">
            <div styleName="activationLinkIcon" />
            <div>
              <span>
                An error occurred while activating your account, please share the following
                information with your landlord.
              </span>
              <span styleName="activationError">Error: {activationLinkRequest.failureReason}</span>
              {Object.keys(activationLinkRequest.failureDetails).length > 0 && (
                <span styleName="activationErrorDetails">
                  Details: {JSON.stringify(activationLinkRequest.failureDetails)}
                </span>
              )}
            </div>
          </div>
        )}
        <div styleName="titleBox">
          <span styleName="greenText">
            {isTenantScreeningPartner ? 'Set up utilities' : 'Required to set up utilities'}
          </span>
          <span>for your new home.</span>
        </div>

        <div styleName="contentBox">
          <SrUtilityAddressSearchForm
            onSubmit={onAddressSearchSubmit}
            onAddressNotFound={trackFailedSearch}
            dispatch={dispatch}
            utilityUser={utilityUser}
            isTenantScreeningPartner={isTenantScreeningPartner}
            utilityPartnerId={utilityPartner.id}
          />
        </div>

        {(recentlyUnlistedAddresses || []).length > 0 && (
          <div styleName="contentBox">
            <SrUtilityRecentlyUnlistedAddresses
              onAddressClick={onAddressSearchSubmit}
              recentlyUnlistedAddresses={recentlyUnlistedAddresses}
            />
          </div>
        )}
      </div>
    </SrUtilitySetupPage>
  );
};

SrUtilitySetupAddressSearchPage.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  utilityUser: PropTypes.object,
  recentlyUnlistedAddresses: PropTypes.array,
  activationLinkRequest: PropTypes.object,
};
SrUtilitySetupAddressSearchPage.displayName = 'SrUtilitySetupAddressSearchPage';

export default SrUtilitySetupAddressSearchPage;
